import { createSelector } from '@reduxjs/toolkit';

import { filtersChoiceSelector } from '@/features/choice/selectors';
import sortFilterCollectionItems from '@/features/filters/helpers/sortFilterCollectionItems';
import { RootStateType } from '@/features/store';
import { IChoiceFilters } from '@/interfaces/choice.interfaces';
import { FilterBlocksEnum } from '@/interfaces/constructor.interfaces';
import {
  FiltersDataType,
  IExtendedFilterCollectionItem,
} from '@/interfaces/filters.interfaces';
import instanceOfReference from '@/utils/instanceOfReference';

import { FilterItemType, GroupedFilterItemType } from '../types';

import filterItemByKeySelector from './itemByKey';

const makeGroupedFilterItem = (
  filterItem: FilterItemType,
  filtersChoice: IChoiceFilters,
  filtersData?: FiltersDataType,
): GroupedFilterItemType[] => {
  const {
    type,
    value,
    props,
    groupByKey: groupingFilterKey,
    isGroupFulfilled,
  } = filterItem;
  if (!isGroupFulfilled || !filtersData || !groupingFilterKey) {
    return [filterItem];
  }

  // Возможные значения фильтра, по которому группируют
  const groupingFilterItemValues = [filtersData?.[groupingFilterKey]].flat();
  // Возможные значения фильтра, который группируют
  const filterItemValues = [filtersData?.[filterItem.key]].flat();

  // Выбранные значения фильтра, по которому группируют
  const groupingFilterItemChoiceValues = filtersChoice?.[groupingFilterKey];

  const isExistValues = !!groupingFilterItemValues.length && !!filterItemValues.length;
  const isReference =
    isExistValues &&
    instanceOfReference(groupingFilterItemValues?.[0]) &&
    instanceOfReference(filterItemValues?.[0]);

  // Группировка работает только для фильтров, которые являются Reference
  if (!isReference) {
    return [filterItem];
  }

  const resolvedFilterItemValues = [
    ...filterItemValues,
  ] as IExtendedFilterCollectionItem[];
  const resolvedGroupingFilterItemValues = [
    ...groupingFilterItemValues,
  ] as IExtendedFilterCollectionItem[];

  const isValidGroupingKey = resolvedFilterItemValues.every(
    value => value?.[groupingFilterKey],
  );

  if (!isValidGroupingKey) {
    return [filterItem];
  }

  switch (type) {
    case FilterBlocksEnum.FilterCheckboxGroup:
    case FilterBlocksEnum.FilterModelCheckboxGroup:
    case FilterBlocksEnum.FilterColorPicker:
    case FilterBlocksEnum.FilterDuoColorPicker: {
      return [...resolvedGroupingFilterItemValues]
        .sort(sortFilterCollectionItems)
        .reduce<GroupedFilterItemType[]>((acc, groupingFilterItemValue) => {
          // Сгруппированные значения фильтра
          const groupedFilterItemValues = resolvedFilterItemValues.filter(value => {
            const ref = value?.[groupingFilterKey];

            if (!instanceOfReference(ref)) {
              return false;
            }

            return ref.id === groupingFilterItemValue.id;
          });
          // Сгруппированные значения фильтра, которые были выбраны пользователем
          const groupedFilterItemChoiceValues = groupedFilterItemValues.filter(value => {
            const ref = value?.[groupingFilterKey];

            if (
              !instanceOfReference(ref) ||
              !Array.isArray(groupingFilterItemChoiceValues)
            ) {
              return false;
            }

            return groupingFilterItemChoiceValues.includes(ref.id);
          });
          const groupedFilterItemValueIds = groupedFilterItemValues.map(
            groupingFilterItemValue => groupingFilterItemValue.id,
          );
          const groupedValue = ((value || []) as string[]).filter(id =>
            groupedFilterItemValueIds.includes(id),
          );
          const groupedOptions = ((props?.options || []) as any[]).filter(option =>
            groupedFilterItemValueIds.includes(option.value),
          );

          // Сгруппированные значения фильтра
          const isAllOptionsDisabled = groupedOptions.every(option => option.disabled);

          const isSelectedGroupedFilterValues = !!groupedValue.length;
          const isSelectedGroupingFilterValues = !!groupedFilterItemChoiceValues.length;

          // Скрывать группу, если
          // 1. Все значения фильтра для группы заблокированы
          // 2. Нет ни одного значения выбранного из данной группы и
          // нет ни одного значения выбранного из фильтра, по которому происходит группировка
          if (
            isAllOptionsDisabled ||
            (!isSelectedGroupedFilterValues && !isSelectedGroupingFilterValues)
          ) {
            return acc;
          }

          return [
            ...acc,
            {
              ...filterItem,
              groupTitle: groupingFilterItemValue.displayName,
              value: groupedValue,
              props: {
                ...props,
                options: groupedOptions,
              },
            },
          ];
        }, []);
    }
    case FilterBlocksEnum.FilterSelect:
    case FilterBlocksEnum.FilterMultipleSelect:
    case FilterBlocksEnum.FilterLocation:
    case FilterBlocksEnum.FilterSlider:
    case FilterBlocksEnum.FilterRangeSlider:
    default:
      return [filterItem];
  }
};

/**
 * Получить фильтр сгруппированный по другому фильтру
 */
const groupedFilterItemSelector = createSelector(
  [
    (_: RootStateType, { filtersData }: { filtersData?: FiltersDataType }) => filtersData,
    filterItemByKeySelector,
    filtersChoiceSelector,
  ],
  (filtersData, filterItem, filtersChoice) => {
    if (!filterItem) {
      return;
    }
    return makeGroupedFilterItem(filterItem, filtersChoice, filtersData);
  },
);

export default groupedFilterItemSelector;
